import { LockIcon, WarningIcon } from "./icons";
import { Notice } from "./Notice";

/**
 * The account/persona/catalogue block reasons this notice renders. The channel
 * reasons ("channel_disabled" / "persona_no_channel") are handled by
 * ChannelUnavailableNotice instead, so they're excluded here.
 */
export type AccountBlockReason =
  | "inactive"
  | "suspended"
  | "archived"
  | "invited"
  | "purged"
  | "notfound"
  | "persona_changed"
  | "catalogue_changed";

interface AccessBlockedNoticeProps {
  /** Why the surface is blocked (from the per-request access re-validation). */
  readonly reason: AccountBlockReason;
}

interface NoticeCopy {
  title: string;
  message: string;
  /** true → lock icon (access removed); false → warning icon (settings changed). */
  locked: boolean;
}

/**
 * Copy per block reason. These are definitive account/access states changed in
 * AiVA Studio, so — unlike the transient connection-lost notice — they carry no
 * Retry button (mirroring AccountNotFoundNotice / ChannelUnavailableNotice).
 * Recovery is by reopening the task pane once access is restored in Studio.
 */
const COPY: Record<AccountBlockReason, NoticeCopy> = {
  inactive: {
    title: "Account deactivated",
    message:
      "This account has been deactivated in AiVA Studio. Contact an administrator to reactivate it.",
    locked: true,
  },
  suspended: {
    title: "Account suspended",
    message:
      "This account has been suspended in AiVA Studio. Contact an administrator if this is unexpected.",
    locked: true,
  },
  archived: {
    title: "Account archived",
    message:
      "This account has been archived in AiVA Studio. Contact an administrator to restore it.",
    locked: true,
  },
  invited: {
    title: "Account not activated",
    message:
      "This account has not been activated in AiVA Studio yet. Contact an administrator to complete setup.",
    locked: true,
  },
  purged: {
    title: "Account removed",
    message:
      "This account has been removed from AiVA Studio. Contact an administrator if this is unexpected.",
    locked: true,
  },
  notfound: {
    title: "Account not found",
    message:
      "This account no longer exists in AiVA Studio. Contact an administrator to have it restored.",
    locked: true,
  },
  persona_changed: {
    title: "Persona changed",
    message:
      "Your persona has been changed in AiVA Studio. Reopen the task pane to load your updated skills and modules.",
    locked: false,
  },
  catalogue_changed: {
    title: "Settings updated",
    message:
      "Your AiVA modules or connections have changed in AiVA Studio. Reopen the task pane to load the latest.",
    locked: false,
  },
};

/**
 * Full-pane state shown when the access guard detects the signed-in user was
 * revoked or their access changed mid-session (see accessGuard.ts). It replaces
 * the entire functional surface. There is no Retry button — this is a definitive
 * state; the user reopens the task pane once access is restored in Studio. (A
 * Retry that reloads is reserved for the transient connection-lost notice.)
 */
export function AccessBlockedNotice({
  reason,
}: Readonly<AccessBlockedNoticeProps>) {
  const { title, message, locked } = COPY[reason];
  return <Notice icon={locked ? <LockIcon /> : <WarningIcon />} title={title} message={message} />;
}
